/**
 * ToolCallCard — visual card showing tool execution and results in the chat.
 *
 * Renders different styles based on tool type:
 * - Data tools: summary with key metrics
 * - UI actions: clickable chips to re-trigger navigation
 * - Exceedances: mini bar showing wells exceeding
 */
import { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/20/solid';
import type { ActiveToolCall } from '@/store/terra';
import { dispatchTerraAction, navigateToPage } from '@/lib/terraBridge';

// Human-readable tool names + icons
export const TOOL_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  list_sites: { label: 'Listing Sites', icon: '🏢', color: 'cyan' },
  query_chemistry: { label: 'Querying Chemistry Data', icon: '🧪', color: 'cyan' },
  query_gauging: { label: 'Querying Water Levels', icon: '💧', color: 'blue' },
  get_site_summary: { label: 'Loading Site Summary', icon: '📊', color: 'cyan' },
  check_regulatory_limits: { label: 'Checking Regulatory Limits', icon: '⚖️', color: 'amber' },
  get_predictions: { label: 'Loading ML Predictions', icon: '🔮', color: 'purple' },
  get_trend_analysis: { label: 'Analyzing Trends', icon: '📈', color: 'green' },
  search_documents: { label: 'Searching Documents', icon: '📄', color: 'mono' },
  get_exceedances: { label: 'Checking Exceedances', icon: '⚠️', color: 'red' },
  create_alert: { label: 'Creating Alert', icon: '🔔', color: 'amber' },
  explain_concept: { label: 'Researching', icon: '📚', color: 'blue' },
  search_similar_documents: { label: 'Searching Knowledge Base', icon: '🔍', color: 'purple' },
  generate_report_section: { label: 'Generating Report', icon: '📝', color: 'green' },
  navigate_page: { label: 'Navigating', icon: '🔗', color: 'cyan' },
  navigate_chart: { label: 'Opening Chart', icon: '📊', color: 'cyan' },
  apply_filters: { label: 'Applying Filters', icon: '🎯', color: 'cyan' },
  highlight_wells: { label: 'Highlighting Wells', icon: '📍', color: 'cyan' },
};

interface Props {
  toolCall: ActiveToolCall;
}

export function ToolCallCard({ toolCall }: Props) {
  const [expanded, setExpanded] = useState(false);
  const config = TOOL_CONFIG[toolCall.name] || { label: toolCall.name, icon: '⚙️', color: 'mono' };

  // UI action tools get a special clickable chip style
  const isUIAction = ['navigate_chart', 'navigate_page', 'apply_filters', 'highlight_wells'].includes(toolCall.name);

  if (isUIAction && !toolCall.isActive) {
    return <UIActionChip toolCall={toolCall} config={config} />;
  }

  return (
    <div className="my-1.5 rounded-md border border-mono-700/40 bg-mono-800/30 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-mono-300 hover:bg-mono-800/50 transition-colors"
      >
        {/* Status indicator */}
        {toolCall.isActive ? (
          <span className="w-3 h-3 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin flex-shrink-0" />
        ) : (
          <span className="flex-shrink-0 text-sm">{config.icon}</span>
        )}

        <span className="flex-1 text-left font-medium">{config.label}</span>

        {toolCall.durationMs !== undefined && (
          <span className="text-mono-500 text-[10px]">{toolCall.durationMs}ms</span>
        )}

        {expanded ? (
          <ChevronDownIcon className="w-3.5 h-3.5 text-mono-500" />
        ) : (
          <ChevronRightIcon className="w-3.5 h-3.5 text-mono-500" />
        )}
      </button>

      {/* Summary line */}
      {toolCall.summary && !expanded && (
        <div className="px-3 pb-1.5 text-[10px] text-mono-400 -mt-1">
          {toolCall.summary}
        </div>
      )}

      {/* Expanded: show input params */}
      {expanded && (
        <div className="px-3 pb-2 border-t border-mono-700/30">
          {toolCall.summary && (
            <div className="text-[10px] text-mono-400 mt-1.5 mb-1">
              {toolCall.summary}
            </div>
          )}
          <pre className="text-[10px] text-mono-400 bg-mono-900/40 rounded p-2 mt-1 overflow-x-auto font-mono">
            {JSON.stringify(toolCall.input, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}


/**
 * Clickable chip for UI actions (navigate, filter, highlight).
 * Re-triggers the action when clicked.
 */
function UIActionChip({ toolCall, config }: { toolCall: ActiveToolCall; config: { label: string; icon: string; color: string } }) {
  const handleClick = () => {
    const input = toolCall.input as Record<string, any>;

    if (toolCall.name === 'navigate_page') {
      navigateToPage(input.path || '/data-vault');
    } else if (toolCall.name === 'navigate_chart') {
      // Navigate to data vault first if needed, then set chart
      dispatchTerraAction({
        type: 'navigate',
        tab: input.tab || 'analytical',
        chartType: input.chart_type,
        wellNames: input.well_names,
        analyteCodes: input.analyte_codes,
      });
    } else if (toolCall.name === 'apply_filters') {
      dispatchTerraAction({ type: 'filter', filters: input });
    } else if (toolCall.name === 'highlight_wells') {
      dispatchTerraAction({ type: 'highlight', wellNames: input.well_names || [] });
    }
  };

  // Build a descriptive label
  const input = toolCall.input as Record<string, any>;
  let chipLabel = config.label;
  if (toolCall.name === 'navigate_chart' && input.chart_type) {
    chipLabel = `View: ${formatChartName(input.chart_type)}`;
  } else if (toolCall.name === 'navigate_page' && input.path) {
    chipLabel = `Go to: ${formatPageName(input.path)}`;
  } else if (toolCall.name === 'apply_filters') {
    const parts: string[] = [];
    if (input.selected_analytes?.length) parts.push(input.selected_analytes.join(', '));
    if (input.selected_wells?.length) parts.push(`${input.selected_wells.length} wells`);
    chipLabel = parts.length ? `Filter: ${parts.join(' + ')}` : 'Apply Filters';
  }

  return (
    <button
      onClick={handleClick}
      className="my-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs
        border border-cyan-500/30 bg-cyan-500/5 text-cyan-300
        hover:bg-cyan-500/15 hover:border-cyan-500/50 transition-colors cursor-pointer"
    >
      <span>{config.icon}</span>
      <span>{chipLabel}</span>
      <ArrowTopRightOnSquareIcon className="w-3 h-3 opacity-50" />
      {toolCall.durationMs !== undefined && (
        <span className="text-mono-500 text-[10px] ml-1">{toolCall.durationMs}ms</span>
      )}
    </button>
  );
}


function formatChartName(chartType: string): string {
  const names: Record<string, string> = {
    'area-series': 'Concentration Timeseries',
    'water-levels': 'Water Levels',
    'box-plot': 'Distribution',
    'exceedance-heatmap': 'Exceedance Heatmap',
    'regression': 'Trend Regression',
    'sparkline-grid': 'Sparkline Grid',
    'ranked-bar': 'Ranked Bar',
    'compliance-matrix': 'Compliance Matrix',
    'well-map': 'Well Map',
    'bubble-map': 'Bubble Map',
    'radar': 'Radar',
    'scatter': 'Scatter',
    'fan': 'Confidence Bands',
    'sankey': 'Sankey',
    'waterfall': 'Waterfall',
    'violin': 'Violin Plot',
    'radial-gauge': 'Risk Gauge',
    'piper': 'Piper Diagram',
  };
  return names[chartType] || chartType.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatPageName(path: string): string {
  const names: Record<string, string> = {
    '/polygon/dashboard': 'Polygon Dashboard',
    '/phreatic/dashboard': 'Phreatic Dashboard',
    '/data-vault': 'Data Vault',
    '/data-vault/charts': 'Data Vault Charts',
    '/pathfinder': 'Pathfinder',
    '/precept': 'Precept',
    '/protocol': 'Protocol',
    '/dashboard': 'Dashboard',
  };
  return names[path] || path.split('/').pop()?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || path;
}
